import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTasksStore } from '@/stores/tasks';
import { http } from '@/lib/http';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Editor } from '@/components/editor/Monaco';
import { useUiStore } from '@/stores/ui';
import { cn } from '@/lib/utils';
import mermaid from 'mermaid';

interface FileContext {
  path: string;
  content: string;
}

// Type for the file tree from /api/files/tree
interface FileTreeNode {
  path: string;
  name: string;
  type: 'file' | 'directory';
  children?: FileTreeNode[];
}

// Flatten file tree to a list of paths
const flattenFileTree = (nodes: FileTreeNode[]): string[] => {
  const filePaths: string[] = [];
  const traverse = (node: FileTreeNode) => {
    if (node.type === 'file') {
      filePaths.push(node.path);
    } else if (node.children) {
      node.children.forEach(traverse);
    }
  };
  nodes.forEach(traverse);
  return filePaths.sort();
};

export default function ContextFetchTestPage() {
  const { byId: tasksById, list: taskIds } = useTasksStore();
  const tasks = taskIds.map(id => tasksById[id]).filter(Boolean);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [fetchedContext, setFetchedContext] = useState<FileContext[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const setHeaderTitle = useUiStore((state) => state.setHeaderTitle);

  // New state for import graph debugging
  const [projectFiles, setProjectFiles] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [mermaidGraph, setMermaidGraph] = useState<string | null>(null);
  const [isGraphLoading, setIsGraphLoading] = useState(false);
  const [mermaidError, setMermaidError] = useState<string | null>(null);

  useEffect(() => {
    setHeaderTitle("Context Fetch & Import Graph Test");
    
    // Fetch project files for the dropdown
    const fetchFiles = async () => {
      try {
        const response = await http<{ tree: FileTreeNode[] }>('/api/files/tree');
        setProjectFiles(flattenFileTree(response.tree));
      } catch (e) {
        console.error("Failed to fetch file tree", e);
        toast.error("Failed to load project files.");
      }
    };
    fetchFiles();
    
    // Initialize Mermaid
    mermaid.initialize({ startOnLoad: false, theme: 'dark' });
  }, [setHeaderTitle]);

  useEffect(() => {
    if (selectedFile) {
      const fetchGraph = async () => {
        setIsGraphLoading(true);
        setMermaidGraph(null);
        setMermaidError(null);
        const toastId = toast.loading(`Fetching import graph for ${selectedFile}...`);
        try {
          const response = await http<{ mermaidGraph: string }>(`/api/indexing/import-tree?file_path=${encodeURIComponent(selectedFile)}`);
          setMermaidGraph(response.mermaidGraph);
          toast.success("Graph fetched.", { id: toastId });
        } catch (e: any) {
          console.error("Failed to fetch import graph", e);
          toast.error("Failed to fetch import graph.", { id: toastId, description: e.message });
          setMermaidError(e.message);
        } finally {
          setIsGraphLoading(false);
        }
      };
      fetchGraph();
    }
  }, [selectedFile]);

  useEffect(() => {
    const renderGraph = async () => {
      if (mermaidGraph) {
        try {
          const container = document.getElementById('mermaid-container');
          if (container) {
            container.innerHTML = ''; // Clear previous graph to prevent duplicates
            const { svg } = await mermaid.render('mermaid-svg', mermaidGraph);
            container.innerHTML = svg;

            container.childNodes[0].style.width = container.childNodes[0].style.maxWidth;

            // Wait for the next paint cycle for the SVG to be in the DOM, then scroll
            setTimeout(() => {
              const selectedNode = container.querySelector('.selected');
              if (selectedNode) {
                selectedNode.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                  inline: 'center',
                });
              }
            }, 100);
          }
        } catch (e: any) {
          console.error("Mermaid rendering error:", e);
          setMermaidError(e.message);
        }
      }
    };
    renderGraph();
  }, [mermaidGraph]);

  const handleFetchContext = async () => {
    if (!selectedTaskId) {
      toast.warning('Please select a task first.');
      return;
    }
    setIsLoading(true);
    setFetchedContext([]);
    const toastId = toast.loading(`Fetching context for task ${selectedTaskId}...`);
    try {
      const context = await http<FileContext[]>('/api/context/from-task', {
        method: 'POST',
        body: JSON.stringify({ taskId: selectedTaskId }),
      });
      setFetchedContext(context);
      toast.success(`Successfully fetched context for ${context.length} file(s).`, { id: toastId });
    } catch (e: any) {
      console.error('Failed to fetch context', e);
      toast.error('Failed to fetch context.', { id: toastId, description: e.message });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col gap-4 p-4 overflow-auto">
      <Card>
        <CardHeader>
          <CardTitle>Test Context Generation from Task</CardTitle>
        </CardHeader>
        <CardContent className="flex items-center gap-4">
          <Select onValueChange={setSelectedTaskId} value={selectedTaskId || ''}>
            <SelectTrigger className="w-[300px]">
              <SelectValue placeholder="Select a task..." />
            </SelectTrigger>
            <SelectContent>
              {tasks.map(task => (
                <SelectItem key={task.id} value={task.id}>
                  {task.title || `Task ${task.id}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={handleFetchContext} disabled={!selectedTaskId || isLoading}>
            {isLoading ? 'Fetching...' : 'Fetch Context'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Debug Import Graph</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <Select onValueChange={setSelectedFile} value={selectedFile || ''}>
            <SelectTrigger className="w-[400px]">
              <SelectValue placeholder="Select a file to see its import graph..." />
            </SelectTrigger>
            <SelectContent className="overflow-y-auto max-h-[30rem]">
              {projectFiles.map(file => (
                <SelectItem key={file} value={file}>
                  {file}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div
            className={cn(
              "p-4 border rounded-md bg-background min-h-[400px] max-h-[70vh] relative",
              (isGraphLoading || mermaidError || !mermaidGraph)
                ? "flex items-center justify-center" // Center placeholder content
                : "overflow-auto" // Enable scrolling for the graph itself
            )}
          >
            {isGraphLoading && <div>Loading graph...</div>}
            {mermaidError && <div className="text-destructive">Error rendering graph: {mermaidError}</div>}
            {mermaidGraph && !mermaidError && (
              // This container has no size constraints, so it will grow as large as the SVG needs to be
              <div id="mermaid-container" />
            )}
            {!isGraphLoading && !mermaidGraph && !mermaidError && (
              <div className="text-muted-foreground">Select a file to view its graph.</div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex-1 overflow-auto">
        <h2 className="text-lg font-semibold mb-2">Fetched Context Files ({fetchedContext.length})</h2>
        {fetchedContext.length > 1000000 ? (
          <Accordion type="multiple" className="w-full">
            {fetchedContext.map(file => (
              <AccordionItem value={file.path} key={file.path}>
                <AccordionTrigger>{file.path}</AccordionTrigger>
                <AccordionContent>
                  <div className="h-96 border rounded">
                    <Editor
                      path={file.path}
                      value={file.content}
                      readOnly
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        ) : (
          <div style={{
            background: 'var(--vscode-editor-background)'
          }}>
            {isLoading ? 'Loading...' : 'No context fetched yet. Select a task and click fetch.'}
          </div>
        )}
      </div>
    </div>
  );
}